import { router } from "@inertiajs/react"
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"
import { rewardPath, rewardsPath } from "@/routes"

/**
 * OKS-59 — Per-row kebab for the Brand Rewards list.
 *
 * Items (UX Spec §5):
 *   - Edit    — opens the Edit Sheet via `?edit=:id` URL state.
 *   - Delete  — opens an `AlertDialog`; confirm issues `router.delete`
 *               which the server resolves to a flash + redirect back
 *               to the index.
 *
 * Touch target: shadcn `Button size="icon"` is 36px; we bump to h-11
 * w-11 to stay ≥ 44px on mobile (UX Spec §6 accessibility floor).
 *
 * Hard delete is the controller's posture (PRD OQ-4); no soft-delete
 * branch to render here.
 */
interface RewardRowActionsProps {
  rewardId: number
  rewardTitle: string
  onEdit: (id: number) => void
}

export function RewardRowActions({
  rewardId,
  rewardTitle,
  onEdit,
}: RewardRowActionsProps) {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  function handleDelete() {
    router.delete(rewardPath(rewardId), {
      preserveScroll: true,
      onFinish: () => {
        setConfirmOpen(false)
      },
      onSuccess: () => {
        // The controller redirects back to rewardsPath() on success;
        // Inertia drives the visit. The flash toast fires from the
        // shared `sonner`-from-flash wiring. Nothing FE-side to do.
      },
      onError: () => {
        // Server-side delete failure is rare for hard-delete (no FK
        // pressure until OKS-60). If it happens the controller flash
        // carries the error message and `sonner` surfaces it.
      },
    })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label={t("rewards.row_actions.menu_label")}
              data-testid="reward-row-actions-trigger"
              data-reward-id={rewardId}
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          }
        />
        <DropdownMenuContent
          align="end"
          finalFocus={finalFocus}
          data-testid="reward-row-actions-menu"
        >
          <DropdownMenuItem
            onClick={() => {
              yieldFocusToDialog()
              onEdit(rewardId)
            }}
            className="flex items-center gap-2"
            data-testid="reward-row-action-edit"
          >
            <Pencil className="size-4" aria-hidden="true" />
            {t("rewards.row_actions.edit")}
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              yieldFocusToDialog()
              setConfirmOpen(true)
            }}
            className="flex items-center gap-2"
            data-testid="reward-row-action-delete"
          >
            <Trash2 className="size-4" aria-hidden="true" />
            {t("rewards.row_actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reward-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("rewards.delete_dialog.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block font-medium">{rewardTitle}</span>
              {t("rewards.delete_dialog.body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("rewards.delete_dialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              // eslint-disable-next-line no-restricted-syntax -- closes on click by design (OKS-1023)
              onClick={handleDelete}
              data-testid="reward-delete-confirm"
            >
              {t("rewards.delete_dialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// Re-export so callers can warm any URL helper they need at the
// row-actions site without two imports.
export { rewardPath, rewardsPath }
